import { at } from 'lodash';
import React, { ReactElement, useMemo } from 'react';
import FormInput from '@shared/form/form-input/form-input';
import FormTextarea from '@shared/form/form-textarea/form-textarea';
import FormSelect from '@shared/form/form-select/form-select';
import FormFileUpload from '@shared/form/form-file-upload/form-file-upload';
import Text from '@text';
import { FieldErrors, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormCheckbox from '@shared/form/form-checkbox/form-checkbox';
import ReCAPTCHA from 'react-google-recaptcha';
import ContactFieldsComponents from './contact-fields.components';
import { FieldData, FetchedOptionType } from '../../../types/form.types';
import ContactFormComponents from '../contact-form.components';

interface FieldArrayInterface extends FieldData {
  id: string;
}
interface ContactFieldsContentProps {
  fieldsArray: FieldArrayInterface[];
  errors: FieldErrors;
  isWhiteCase: boolean;
  addSpacing: (id: string) => ReactElement | null;
  customWidth: (id: string) => string | undefined;
  requiredLegendPlaceIndex: number | null;
  isNumberType: (id: string) => boolean;
  fetchErrorMessage?: string;
}

const {
  Container,
  BackgroundContainer,
  SubmitButton,
  CaptchaContainer
} = ContactFieldsComponents;
const { FetchErrorMessage } = ContactFormComponents;

const ContactFieldsContent: React.ForwardRefRenderFunction<
  ReCAPTCHA,
  ContactFieldsContentProps
> = (
  {
    fieldsArray,
    errors,
    isWhiteCase,
    addSpacing,
    customWidth,
    requiredLegendPlaceIndex,
    isNumberType,
    fetchErrorMessage
  },
  recaptchaRef
) => {
  const { getValues } = useFormContext();
  const { t } = useTranslation();

  const isRequired = useMemo(
    () => (field: FieldArrayInterface): boolean => {
      const [case_detailed_reason, case_reason] = getValues([
        'case_detailed_reason',
        'case_reason'
      ]);

      const [requiredValue, requiredConditional] = at(
        field.validation,
        'required',
        `conditional.${case_reason}.${
          case_detailed_reason || '_default'
        }.required`
      );

      return requiredConditional === undefined
        ? requiredValue
        : requiredConditional;
    },
    [getValues]
  );

  const setOptions = useMemo(
    () => (
      // eslint-disable-next-line
      options: FetchedOptionType[] | any,
      name: string
    ): FetchedOptionType[] => {
      if (name === 'sub_category') {
        const [main_category] = getValues(['main_category']);
        return at(options, `${main_category}`)[0];
      }

      return options;
    },
    [getValues]
  );

  return (
    <BackgroundContainer fillBackground={!isWhiteCase}>
      <Container>
        {fieldsArray.length ? (
          <>
            {fieldsArray.map((field, index) => {
              const errorMessage =
                errors && errors[field.id] ? errors[field.id].message : null;

              switch (field.type) {
                case 'textbox':
                  return (
                    <React.Fragment key={`form-input-container-${field.id}`}>
                      <FormInput
                        key={`form-input-${field.id}`}
                        name={field.id}
                        label={field.label}
                        errorMessage={errorMessage}
                        isRequired={isRequired(field)}
                        customWidth={customWidth(field.id)}
                        showRequiredLegend={requiredLegendPlaceIndex === index}
                        isNumberType={isNumberType(field.id)}
                      />
                      {addSpacing(field.id)}
                    </React.Fragment>
                  );
                case 'textarea':
                  return (
                    <React.Fragment key={`form-textarea-container-${field.id}`}>
                      <FormTextarea
                        key={`form-textarea-${field.id}`}
                        name={field.id}
                        label={field.label}
                        errorMessage={errorMessage}
                        isRequired={field.validation.required}
                        showRequiredLegend={requiredLegendPlaceIndex === index}
                      />
                      {addSpacing(field.id)}
                    </React.Fragment>
                  );
                case 'select':
                  return (
                    <React.Fragment key={`form-select-container-${field.id}`}>
                      <FormSelect
                        key={`form-select-${field.id}`}
                        label={field.label}
                        placeholder={
                          field.placeholder
                            ? field.placeholder
                            : t('form.selectSomething')
                        }
                        name={field.id}
                        options={setOptions(field.options, field.id)}
                        errorMessage={errorMessage}
                        isRequired={field.validation.required}
                        reverseColors={!isWhiteCase}
                        customWidth={customWidth(field.id)}
                        showRequiredLegend={requiredLegendPlaceIndex === index}
                      />
                      {addSpacing(field.id)}
                    </React.Fragment>
                  );
                case 'file':
                  return (
                    <React.Fragment key={`form-file-container-${field.id}`}>
                      <FormFileUpload
                        key={`form-file-${field.id}`}
                        name={field.id}
                        label={field.label}
                        errorMessage={errorMessage}
                        isRequired={field.validation.required}
                        showRequiredLegend={requiredLegendPlaceIndex === index}
                      />
                      {addSpacing(field.id)}
                    </React.Fragment>
                  );
                case 'checkbox':
                  return (
                    <React.Fragment key={`form-checkbox-container-${field.id}`}>
                      <FormCheckbox
                        key={`form-checkbox-${field.id}`}
                        name={field.id}
                        label={field.label}
                        link={field.link}
                        errorMessage={errorMessage}
                        isRequired={field.validation.required}
                      />
                      {addSpacing(field.id)}
                    </React.Fragment>
                  );

                default:
                  return null;
              }
            })}

            <CaptchaContainer>
              <ReCAPTCHA
                sitekey={`${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`}
                ref={recaptchaRef}
                size="normal"
              />
            </CaptchaContainer>
            <SubmitButton type="submit">
              <Text tParams="form.submit" />
            </SubmitButton>
          </>
        ) : (
          fetchErrorMessage && (
            <FetchErrorMessage>
              <Text tParams={fetchErrorMessage} />
            </FetchErrorMessage>
          )
        )}
      </Container>
    </BackgroundContainer>
  );
};

export default React.forwardRef(ContactFieldsContent);
